'use strict'

// Constants
import {
    PATH_SEPARATOR,
    COLOR_ID_SEPARATOR,
    CURRENT_CATEGORY_PATH_PREFIX,
    PRODUCT_SET_PATH_PREFIX,
    CURRENT_PRODUCT_PATH_PREFIX,
    OUTLET_PATH_PREFIX,
    LEGACY_CN_LOCALE_ID,
    EN_LOCALE_ID,
    PDP_REGEX_WITH_PRODUCT_PATH,
    PDP_REGEX_WITH_CATEGORY_PATH,
    PDP_REGEX_WITH_DIGITS_EAN,
    PDP_REGEX_CANONICAL,
    PDP_REGEX_WITH_QUERY_PARAM,
    PDP_REGEX_WITH_WB_PREFIX,
    ORDER_CONFIRMATION_PATH,
    ORDER_INVOICE_PATH,
    ORDER_HISTORY_PATH,
    DEFAULT_COMMON_PATH_OBJ
} from '../util/constants'

/**
 * Checks if a given path includes a trailing slash.
 *
 * This function evaluates whether the provided path string ends with a
 * predefined path separator, indicating the presence of a trailing slash.
 *
 * @param {string} path - The path string to be checked for a trailing slash.
 * @returns {boolean} - Returns true if the path ends with a trailing slash; otherwise, false.
 */
export const checkIfPathIncludesTrailingSlash = (path) => {
    return path.endsWith(PATH_SEPARATOR)
}

/**
 * Removes the trailing slash from a given path string.
 *
 * @param {string} path - The path string from which the trailing slash should be removed.
 * @returns {string} The path string without the trailing slash.
 */
export const getPathWithoutTrailingSlash = (path) => {
    return path.slice(0, -1)
}

/**
 * Checks if a given string contains at least one uppercase letter.
 *
 * @param {string} str - The string to be checked for uppercase letters.
 * @returns {boolean} - Returns true if the string contains at least one uppercase letter, otherwise false.
 */
export const hasUpperCaseLetter = (str) => /[A-Z]/.test(str)

/**
 * Checks if a given string contains at least one lowercase letter.
 *
 * @param {string} str - The string to be checked for lowercase letters.
 * @returns {boolean} - Returns true if the string contains at least one lowercase letter, otherwise false.
 */
export const hasLowerCaseLetter = (str) => /[a-z]/.test(str)

/**
 * Constructs and returns a locale-specific path based on the provided parameters.
 * This function adjusts the locale path for outlet sites and handles specific
 * language conditions, such as the Chinese language locale.
 *
 * @param {boolean} isOutletSite - Indicates if the request is from an outlet site.
 * @param {string} websiteCode - The code representing the website.
 * @param {string} language - The language code for the desired locale.
 * @param {string} fullPath - The full path of the current request.
 * @returns {string} The constructed locale path, adjusted for outlet site and language conditions.
 */
export const getLocalePath = (isOutletSite, websiteCode, language, fullPath) => {
    const regex = /\/(outlet-)?(\w{2})\/(\w{2})\/(.+)/
    const match = fullPath.match(regex)

    // Check if the request is from cn language
    let isCN = false
    if (match) {
        isCN = match[3] === LEGACY_CN_LOCALE_ID
    }

    let localePath = `${PATH_SEPARATOR}${websiteCode}${PATH_SEPARATOR}${language}`

    // Adjust locale path depending on the outlet site and cn language condition
    if (isOutletSite && isCN) {
        // Logic for outlet site with CN language
        localePath = `${PATH_SEPARATOR}${OUTLET_PATH_PREFIX}${websiteCode}${PATH_SEPARATOR}${EN_LOCALE_ID}`
    } else if (isOutletSite && !isCN) {
        // Logic for outlet site with non-CN language
        localePath = `${PATH_SEPARATOR}${OUTLET_PATH_PREFIX}${websiteCode}${PATH_SEPARATOR}${language}`
    } else if (!isOutletSite && isCN) {
        // Logic for non-outlet site with CN language
        localePath = `${PATH_SEPARATOR}${websiteCode}${PATH_SEPARATOR}${EN_LOCALE_ID}`
    } else {
        // Logic for non-outlet site with non-CN language
        localePath = `${PATH_SEPARATOR}${websiteCode}${PATH_SEPARATOR}${language}`
    }

    // Construct the redirection URL based on the request's origin.
    return localePath
}

/**
 * Constructs and returns a URL for redirecting to a product detail page (PDP) with query parameters.
 *
 * This function is used to generate a localized URL for a product detail page based on the provided
 * regex match results and the full path. It determines if the request is from an outlet site, extracts
 * necessary components such as website code, language, category, master product ID, and color variation,
 * and constructs the URL accordingly.
 *
 * @param {Array} pdpRegexWithQueryParamMatch - An array containing regex match results for the PDP URL.
 *        The array is expected to have the following structure:
 *        [fullMatch, isOutletSite, websiteCode, language, category, masterID, , colorVariation]
 * @param {string} fullPath - The full path of the current request, used to determine the locale path.
 * @returns {string} - A string representing the constructed URL for redirection to the PDP.
 */
export const redirectPDPUrlWithQueryParam = (pdpRegexWithQueryParamMatch, fullPath) => {
    const isOutletSite = !!pdpRegexWithQueryParamMatch[1] // Check if request is from outlet site
    const websiteCode = pdpRegexWithQueryParamMatch[2]
    const language = pdpRegexWithQueryParamMatch[3]
    const masterID = pdpRegexWithQueryParamMatch[5]
    const colorVariation = pdpRegexWithQueryParamMatch[7]

    const localePath = getLocalePath(isOutletSite, websiteCode, language, fullPath)

    // {site}/{locale}/product/[id-of-the-product-master]-[id-color]
    return (
        `${localePath}${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}`.toLowerCase() +
        `${masterID}${COLOR_ID_SEPARATOR}${colorVariation}`.toUpperCase()
    )
}

/**
 * Constructs and returns a product detail page (PDP) URL with the appropriate website and language prefix.
 *
 * This function is used to redirect to a PDP URL that includes the website and language prefix,
 * ensuring the correct locale path is used based on the provided regex match and full path.
 *
 * @param {Array} pdpRegexWithWBPrefixMatch - An array containing regex matches for the PDP URL components.
 *   - [0] {string} - The full matched string (not used directly).
 *   - [1] {string} - Indicator if the request is from an outlet site.
 *   - [2] {string} - The website code.
 *   - [3] {string} - The language code.
 *   - [4] {string} - The category ID of the primary category of the product.
 *   - [5] {string} - The master ID of the product.
 * @param {string} fullPath - The full path of the current request URL.
 * @returns {string} - The constructed PDP URL with the website and language prefix, formatted in lowercase.
 */
export const redirectPDPURLWithWBPrefix = (pdpRegexWithWBPrefixMatch, fullPath) => {
    const isOutletSite = !!pdpRegexWithWBPrefixMatch[1] // Check if request is from outlet site
    const websiteCode = pdpRegexWithWBPrefixMatch[2]
    const language = pdpRegexWithWBPrefixMatch[3]
    const masterID = pdpRegexWithWBPrefixMatch[5]
    const colorVariation = pdpRegexWithWBPrefixMatch[10]

    const localePath = getLocalePath(isOutletSite, websiteCode, language, fullPath)

    // {site}/{locale}/product//[id-of-the-product-master]
    // {site}/{locale}/product//[id-of-the-product-master]-[id-color]
    return (
        `${localePath}${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}`.toLowerCase() +
        `${masterID}`.toUpperCase() +
        (colorVariation ? `${COLOR_ID_SEPARATOR}${colorVariation}`.toUpperCase() : '')
    )
}

/**
 * Constructs a normalized product detail page (PDP) URL using extracted EAN and locale information.
 *
 * @param {Array} pdpRegexWithDigitsEANMatch - The regex match array containing outlet site flag, website code, language, and EAN.
 * @param {string} fullPath - The original request path.
 * @returns {string} The formatted and lowercased PDP URL in the form: {site}/{locale}/product/product_EAN.
 */
export const redirectPDPWithDigitsEAN = (pdpRegexWithDigitsEANMatch, fullPath) => {
    const isOutletSite = !!pdpRegexWithDigitsEANMatch[1] // Check if request is from outlet site
    const websiteCode = pdpRegexWithDigitsEANMatch[2]
    const language = pdpRegexWithDigitsEANMatch[3]
    const ean = pdpRegexWithDigitsEANMatch[5]

    const localePath = getLocalePath(isOutletSite, websiteCode, language, fullPath)

    // {site}/{locale}/product/product_EAN
    return `${localePath}${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}${ean}`.toLowerCase()
}

/**
 * Constructs and returns a canonical Product Detail Page (PDP) URL based on regex match groups and the full request path.
 *
 * @param {Array} pdpRegexCanonicalMatch - Array of regex match results containing site, website code, language, and master product ID.
 * @param {string} fullPath - The full original request path.
 * @returns {string} The canonical PDP URL in lowercase, formatted as {site}/{locale}/product/{master_ID}.
 */
export const redirectPDPWithCanonicalURL = (pdpRegexCanonicalMatch, fullPath) => {
    const isOutletSite = !!pdpRegexCanonicalMatch[1] // Check if request is from outlet site
    const websiteCode = pdpRegexCanonicalMatch[2]
    const language = pdpRegexCanonicalMatch[3]
    const masterID = pdpRegexCanonicalMatch[5]

    const localePath = getLocalePath(isOutletSite, websiteCode, language, fullPath)

    // {site}/{locale}/product/master_ID
    return (
        `${localePath}${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}`.toLowerCase() +
        masterID.toUpperCase()
    )
}

/**
 * Extracts the path from a given URL pathname, excluding the locale segments.
 * The function assumes the pathname follows a pattern where the locale is represented
 * by the first two segments (e.g., '/en-US/'). It returns the remaining part of the path.
 *
 * @param {string} pathname - The full URL pathname from which the locale should be removed.
 * @returns {string|null} The path without the locale segments, or null if the pattern does not match.
 */
export const getPathWithoutLocale = (pathname) => {
    let pathWithoutLocale = null
    const pattern = /\/[^/]+\/[^/]+\/(.+)/
    const match = pathname.match(pattern)

    if (match) {
        pathWithoutLocale = match[1]
    }

    return pathWithoutLocale
}

/**
 * Builds and encodes a URL for a given category ID using predefined path segments.
 * @param {string} categoryId - The unique identifier of the category.
 * @returns {string} The encoded, lowercase URL path for the specified category.
 */
export const categoryUrlBuilder = (categoryId) => {
    return encodeURI(
        `${PATH_SEPARATOR}${CURRENT_CATEGORY_PATH_PREFIX}${PATH_SEPARATOR}${categoryId}`.toLowerCase()
    )
}

/**
 * Builds a master product URL for a given product ID, formatting the path and product ID appropriately.
 * @param {string} productId - The unique identifier of the product.
 * @returns {string} The encoded URL string for the master product.
 */
export const masterProductURLBuilder = (productId) => {
    return encodeURI(
        `${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}`.toLowerCase() +
            productId.toUpperCase()
    )
}

/**
 * Builds a SEO-friendly URL for a product variation group based on product and color IDs.
 * @param {string} productId - The unique identifier for the product.
 * @param {string} colorId - The unique identifier for the color variation.
 * @returns {string} The encoded URL string representing the product variation group.
 */
export const variationGroupURLBuilder = (productId, colorId) => {
    return encodeURI(
        `${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}`.toLowerCase() +
            `${productId}${COLOR_ID_SEPARATOR}${colorId}`.toUpperCase()
    )
}

/**
 * Constructs a URL for a product set by encoding the product set identifier.
 *
 * This function generates a URL path for a product set by appending the product set identifier
 * to a predefined path prefix and separator. The resulting string is converted to lowercase
 * and encoded to ensure it is a valid URI component.
 *
 * @param {string} productSetId - The unique identifier for the product set.
 * @returns {string} The encoded URL path for the specified product set.
 */
export const productSetURLBuilder = (productSetId) => {
    return encodeURI(
        `${PATH_SEPARATOR}${PRODUCT_SET_PATH_PREFIX}${PATH_SEPARATOR}${productSetId}`.toLocaleLowerCase()
    )
}

/**
 * Determines if a given path corresponds to a product page path.
 *
 * This function checks if the provided path matches either the product detail page (PDP) regex
 * with a product path or a category path. It returns a boolean indicating whether the path is
 * recognized as a product path.
 *
 * @param {string} path - The URL path to be evaluated.
 * @returns {boolean} - Returns true if the path matches a product page pattern, otherwise false.
 */
export const isProductPath = (path) => {
    if (!path) return false

    return (
        path.match(PDP_REGEX_WITH_PRODUCT_PATH) ||
        path.match(PDP_REGEX_WITH_CATEGORY_PATH) ||
        path.match(PDP_REGEX_WITH_DIGITS_EAN) ||
        path.match(PDP_REGEX_WITH_QUERY_PARAM) ||
        path.match(PDP_REGEX_WITH_WB_PREFIX) ||
        path.match(PDP_REGEX_CANONICAL)
    )
}

/**
 * Determines if a given path matches either the order confirmation or order history paths.
 *
 * @param {string} path - The URL path to check.
 * @returns {boolean} True if the path matches an order confirmation or order history path, otherwise false.
 */
export const isOrderPath = (path) => {
    if (!path) return false

    return (
        path.match(ORDER_CONFIRMATION_PATH) ||
        path.match(ORDER_HISTORY_PATH) ||
        path.match(ORDER_INVOICE_PATH)
    )
}

/**
 * Determines if a given URL path includes a query parameter.
 *
 * This function checks if the provided path contains a '?' character,
 * which typically indicates the presence of query parameters in a URL.
 *
 * @param {string} path - The URL path to be checked for query parameters.
 * @returns {boolean} - Returns true if the path includes a query parameter ('?'), otherwise false.
 */
export const isIncludesQueryParam = (path) => {
    if (!path) return false

    return path.includes('?')
}

/**
 * Splits a URL path into its base path and query parameters.
 *
 * This function takes a URL path as input and separates it into two parts:
 * the path without query parameters and the query parameters themselves.
 * If the input path is undefined or empty, it returns an object with an empty
 * path and undefined query parameters.
 *
 * @param {string} path - The URL path to be split, potentially containing query parameters.
 * @returns {Object} An object containing:
 *   - {string} pathWithoutQueryParams: The base path without any query parameters.
 *   - {string|undefined} queryParams: The query parameters as a string, or undefined if none exist.
 */
export const splitQueryParams = (path) => {
    if (!path) {
        return {pathWithoutQueryParams: '', queryParams: undefined}
    }
    const [pathWithoutQueryParams, queryParams] = path.split('?')
    return {pathWithoutQueryParams, queryParams}
}

/**
 * Constructs a query string prefix from the provided query parameters.
 * @param {string} queryParams - The query parameters to append to the URL.
 * @returns {string} The query string prefixed with '?' if queryParams exist, otherwise an empty string.
 */
export const getQueryParamPart = (queryParams) => {
    return queryParams ? '?' + queryParams : ''
}

/**
 * Parses common path parameters from a regex match array.
 * @param {Array} match - The regex match array, where:
 *   match[1] indicates if the site is an outlet site,
 *   match[2] contains the website code,
 *   match[3] contains the language code.
 * @returns {Object} An object containing:
 *   {boolean} isOutletSite - True if the site is an outlet site.
 *   {string} websiteCode - The extracted website code.
 *   {string} language - The extracted language code.
 */
const parseCommonPathsFromRegex = (match) => {
    const isOutletSite = !!match[1]
    const websiteCode = match[2]
    const language = match[3]

    return {
        isOutletSite,
        websiteCode,
        language
    }
}

/**
 * Parses and returns the identifier from a regex match, optionally removing query parameters from a URL.
 * @param {string} match - The string matched by the regex, potentially containing query parameters.
 * @param {boolean} isURLIncludesQueryParam - Flag indicating whether to remove query parameters from the match.
 * @returns {string} The identifier, either as-is or with query parameters removed.
 */
const parseIdentifierFromRegex = (match, isURLIncludesQueryParam) => {
    return isURLIncludesQueryParam ? splitQueryParams(match).pathWithoutQueryParams : match
}

/**
 * Constructs a redirection URL, optionally appending query parameters if present.
 * @param {boolean} isURLIncludesQueryParam - Indicates whether the original URL includes query parameters.
 * @param {string} prebuiltUrlRule - The base URL or URL rule to which query parameters may be appended.
 * @param {string} match - The matched URL segment used to extract query parameters.
 * @returns {string} The constructed redirection URL, with query parameters appended if applicable.
 */
const buildRedirectionURLWithQueryParam = (isURLIncludesQueryParam, prebuiltUrlRule, match) => {
    return isURLIncludesQueryParam
        ? prebuiltUrlRule + '?' + splitQueryParams(match).queryParams
        : prebuiltUrlRule
}

/**
 * Handles the inverse rule for product URLs by generating a redirection URL
 * based on the given path. This function checks for specific patterns in the
 * path and constructs a new URL if certain conditions are met, such as the
 * presence of uppercase letters or trailing slashes.
 *
 * @param {string} path - The URL path to be processed for redirection.
 * @returns {string|null} - Returns a redirection URL if the path matches
 *                          specific conditions, otherwise returns null.
 */
export const handleProductInverseRule = (path) => {
    let redirectionURL = null
    let isURLIncludesQueryParam = null
    let commonPaths = DEFAULT_COMMON_PATH_OBJ

    const pdpRegexWithProductPathMatch = path.match(PDP_REGEX_WITH_PRODUCT_PATH)
    const pdpRegexWithCategoryPathMatch = path.match(PDP_REGEX_WITH_CATEGORY_PATH)
    const isIncludesTrailingSlash = checkIfPathIncludesTrailingSlash(path)

    /*
        Cases: 
        - /eu/en/product/HILC3Q02-605?color=605&size=M&pid=7613409899149
        - /eu/en/product/HILC3Q02-605
        - /eu/en/product/HILC3Q02
    */
    if (pdpRegexWithProductPathMatch) {
        commonPaths = parseCommonPathsFromRegex(pdpRegexWithProductPathMatch)
        const pathProduct = pdpRegexWithProductPathMatch[4]
        isURLIncludesQueryParam = isIncludesQueryParam(pdpRegexWithProductPathMatch[5])

        const productID = parseIdentifierFromRegex(
            pdpRegexWithProductPathMatch[5],
            isURLIncludesQueryParam
        )

        if (
            hasUpperCaseLetter(commonPaths.websiteCode) ||
            hasUpperCaseLetter(commonPaths.language) ||
            hasUpperCaseLetter(pathProduct) ||
            hasLowerCaseLetter(productID) ||
            isIncludesTrailingSlash
        ) {
            const localePath = getLocalePath(
                commonPaths.isOutletSite,
                commonPaths.websiteCode,
                commonPaths.language,
                path
            ).toLowerCase()

            const productURLRuleWithoutCategory = `${localePath}${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}${productID.toUpperCase()}`

            redirectionURL = buildRedirectionURLWithQueryParam(
                isURLIncludesQueryParam,
                productURLRuleWithoutCategory,
                pdpRegexWithProductPathMatch[5]
            )
        }
        /*
            Cases:
            - /eu/en/p/men-swimwear/MSEH2F45-990?color=990&size=M&pid=7613409559173
            - /eu/en/p/men-swimwear/MSEH2F45-990
            - /eu/en/p/men-swimwear/MSEH2F45
        */
    } else if (pdpRegexWithCategoryPathMatch) {
        commonPaths = parseCommonPathsFromRegex(pdpRegexWithCategoryPathMatch)
        const pathP = pdpRegexWithCategoryPathMatch[4]
        const primaryCategoryID = pdpRegexWithCategoryPathMatch[5]
        isURLIncludesQueryParam = isIncludesQueryParam(pdpRegexWithCategoryPathMatch[6])
        const productID = parseIdentifierFromRegex(
            pdpRegexWithCategoryPathMatch[6],
            isURLIncludesQueryParam
        )

        if (
            hasUpperCaseLetter(commonPaths.websiteCode) ||
            hasUpperCaseLetter(commonPaths.language) ||
            hasUpperCaseLetter(pathP) ||
            hasUpperCaseLetter(primaryCategoryID) ||
            hasLowerCaseLetter(productID) ||
            isIncludesTrailingSlash
        ) {
            const localePath = getLocalePath(
                commonPaths.isOutletSite,
                commonPaths.websiteCode,
                commonPaths.language,
                path
            ).toLowerCase()

            const productURLRuleWithCategory = `${localePath}${PATH_SEPARATOR}${CURRENT_PRODUCT_PATH_PREFIX}${PATH_SEPARATOR}${productID.toUpperCase()}`

            redirectionURL = buildRedirectionURLWithQueryParam(
                isURLIncludesQueryParam,
                productURLRuleWithCategory,
                pdpRegexWithCategoryPathMatch[6]
            )
        }
    }

    return redirectionURL
}

/**
 * Handles the normalization and redirection logic for order-related URL paths to enforce SEO rules.
 * This function checks if the provided path matches order confirmation, order history, or invoice patterns,
 * and determines if a redirection is needed based on case sensitivity, trailing slashes, and query parameters.
 * If normalization is required (e.g., uppercase/lowercase issues, trailing slash present), it constructs the
 * appropriate redirection URL.
 *
 * @param {string} path - The URL path to be checked and potentially normalized.
 * @returns {string|null} The normalized redirection URL if a redirect is needed, or null if no redirection is required.
 */
const handleOrderInverseRule = (path) => {
    let redirectionURL = null
    let isURLIncludesQueryParam = null
    let commonPaths = DEFAULT_COMMON_PATH_OBJ

    const orderConfirmationMatch = path.match(ORDER_CONFIRMATION_PATH)
    const orderHistoryMatch = path.match(ORDER_HISTORY_PATH)
    const orderInvoiceMatch = path.match(ORDER_INVOICE_PATH)

    const isIncludesTrailingSlash = checkIfPathIncludesTrailingSlash(path)

    /*
        Case: /eu/en/checkout/confirmation/${ORDER_ID}
    */
    if (orderConfirmationMatch) {
        commonPaths = parseCommonPathsFromRegex(orderConfirmationMatch)
        const checkoutConst = orderConfirmationMatch[4]
        const confirmationConst = orderConfirmationMatch[5]
        isURLIncludesQueryParam = isIncludesQueryParam(orderConfirmationMatch[6])
        const confirmationOrderID = parseIdentifierFromRegex(
            orderConfirmationMatch[6],
            isURLIncludesQueryParam
        )

        if (
            hasUpperCaseLetter(commonPaths.websiteCode) ||
            hasUpperCaseLetter(commonPaths.language) ||
            hasUpperCaseLetter(checkoutConst) ||
            hasUpperCaseLetter(confirmationConst) ||
            hasLowerCaseLetter(confirmationOrderID) ||
            isIncludesTrailingSlash
        ) {
            const localePath = getLocalePath(
                commonPaths.isOutletSite,
                commonPaths.websiteCode,
                commonPaths.language,
                path
            ).toLowerCase()

            const confirmationURLRule = `${localePath}${PATH_SEPARATOR}checkout${PATH_SEPARATOR}confirmation${PATH_SEPARATOR}${confirmationOrderID.toUpperCase()}`

            redirectionURL = buildRedirectionURLWithQueryParam(
                isURLIncludesQueryParam,
                confirmationURLRule,
                orderConfirmationMatch[6]
            )
        }
        /*
            Case: /eu/en/account/orders/${ORDER_ID}
        */
    } else if (orderHistoryMatch) {
        commonPaths = parseCommonPathsFromRegex(orderHistoryMatch)
        const accountConst = orderHistoryMatch[4]
        const ordersConst = orderHistoryMatch[5]
        isURLIncludesQueryParam = isIncludesQueryParam(orderHistoryMatch[6])
        const orderHistoryOrderID = parseIdentifierFromRegex(
            orderHistoryMatch[6],
            isURLIncludesQueryParam
        )

        if (
            hasUpperCaseLetter(commonPaths.websiteCode) ||
            hasUpperCaseLetter(commonPaths.language) ||
            hasUpperCaseLetter(accountConst) ||
            hasUpperCaseLetter(ordersConst) ||
            hasLowerCaseLetter(orderHistoryOrderID) ||
            isIncludesTrailingSlash
        ) {
            const localePath = getLocalePath(
                commonPaths.isOutletSite,
                commonPaths.websiteCode,
                commonPaths.language,
                path
            ).toLowerCase()

            const orderHistoryURLRule = `${localePath}${PATH_SEPARATOR}account${PATH_SEPARATOR}orders${PATH_SEPARATOR}${orderHistoryOrderID.toUpperCase()}`

            redirectionURL = buildRedirectionURLWithQueryParam(
                isURLIncludesQueryParam,
                orderHistoryURLRule,
                orderHistoryMatch[6]
            )
        }
        /*
            Case: /eu/en/invoice/${ORDER_ID}
        */
    } else if (orderInvoiceMatch) {
        commonPaths = parseCommonPathsFromRegex(orderInvoiceMatch)
        const invoiceConst = orderInvoiceMatch[4]
        isURLIncludesQueryParam = isIncludesQueryParam(orderInvoiceMatch[5])
        const orderInvoiceOrderID = parseIdentifierFromRegex(
            orderInvoiceMatch[5],
            isURLIncludesQueryParam
        )

        if (
            hasUpperCaseLetter(commonPaths.websiteCode) ||
            hasUpperCaseLetter(commonPaths.language) ||
            hasUpperCaseLetter(invoiceConst) ||
            hasLowerCaseLetter(orderInvoiceOrderID) ||
            isIncludesTrailingSlash
        ) {
            const localePath = getLocalePath(
                commonPaths.isOutletSite,
                commonPaths.websiteCode,
                commonPaths.language,
                path
            ).toLowerCase()

            const orderHistoryURLRule = `${localePath}${PATH_SEPARATOR}invoice${PATH_SEPARATOR}${orderInvoiceOrderID.toUpperCase()}`

            redirectionURL = buildRedirectionURLWithQueryParam(
                isURLIncludesQueryParam,
                orderHistoryURLRule,
                orderInvoiceMatch[5]
            )
        }
    }

    return redirectionURL
}

/**
 * Determines if a global redirection is needed for a given location object based on SEO rules,
 * such as removing trailing slashes, enforcing lowercase paths (except for product detail pages),
 * and correcting product IDs to uppercase for PDP URLs. Returns the appropriate redirection URL if needed.
 *
 * @param {Object} location - The location object, typically containing `pathname` and `search` properties.
 * @returns {string|null} The URL to redirect to if a redirection is required, or null if no redirection is needed.
 */
export const getGlobalRedirectionURL = (location) => {
    if (!location) return null

    // Global Redirection Rules
    let redirectionURL = null
    let originalPath = location.pathname + location.search

    let {pathWithoutQueryParams, queryParams} = splitQueryParams(originalPath)
    /*
        Redirects to the same path without trailing slash
        i.e. /en-GB/path/ -> /en-GB/path
    */
    if (checkIfPathIncludesTrailingSlash(pathWithoutQueryParams)) {
        redirectionURL = getPathWithoutTrailingSlash(pathWithoutQueryParams)
    }

    const currentPath = redirectionURL || pathWithoutQueryParams
    /*
        Redirects to the same path with lowercase letters if it is not a product path (check inverse rule below)
        i.e. /en-GB/Shoes -> /en-GB/shoes
    */
    if (
        !isProductPath(originalPath) &&
        !isOrderPath(originalPath) &&
        hasUpperCaseLetter(currentPath)
    ) {
        redirectionURL = redirectionURL
            ? redirectionURL.toLowerCase()
            : pathWithoutQueryParams.toLowerCase()
    } else if (isOrderPath(originalPath)) {
        /*
            Special order inverse rule (SFCC APIs are case-sensitive so upper-case order ID should be sent with the API request):
            Handles the inverse rule for order URLs by generating a redirection URL based on the given path.
            It checks if the provided path matches order confirmation, order history, or invoice patterns, and determines if a redirection is needed based on case sensitivity, trailing slashes, and query parameters.
            It returns the normalized redirection URL if a redirect is needed.
        */
        var inverseOrderURL = handleOrderInverseRule(originalPath)
        if (inverseOrderURL) {
            return inverseOrderURL
        }
    } else if (isProductPath(originalPath)) {
        /*
            Special inverse rule:
            PDP URLs, since the variation groups and master products IDs are in uppercase for business and technical (outside SFCC) reasons:
            variation groups, their ID is in uppercase, so any URL to the PDP with lowercase in the ID should be redirected with 301 status to the correct ID (uppercase), the path part of the URL is still in lowercases
            masters (canonical), their ID is in uppercase, so any URL to the PDP with lowercase in the ID should be redirected with 301 status to the correct ID (uppercase), the path part of the URL is still in lowercases
        */
        var inverseProductURL = handleProductInverseRule(originalPath)
        if (inverseProductURL) {
            return inverseProductURL
        }
    }

    if (redirectionURL) {
        return redirectionURL + getQueryParamPart(queryParams)
    }

    return null
}
